import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Search, Target } from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { AUDIENCE_KINDS, filterByWords, formatMinutes, programSummary, programTotals } from "../../lib/training";

// Who a programme is for: the pharmacy roles of Jamora, or Jamora's own team (then no role).
export function AudienceFields({ kind, audience, roles, onChange, idPrefix = "audience" }) {
  const toggle = (code) => onChange({ kind, audience: audience.includes(code) ? audience.filter((c) => c !== code) : [...audience, code] });
  return (
    <div className="space-y-3">
      <RadioGroup value={kind} onValueChange={(value) => onChange({ kind: value, audience })} className="gap-2">
        {Object.entries(AUDIENCE_KINDS).map(([value, label]) => (
          <label key={value} htmlFor={`${idPrefix}-${value}`} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <RadioGroupItem id={`${idPrefix}-${value}`} value={value} data-testid={`${idPrefix}-kind-${value}`} />{label}
          </label>
        ))}
      </RadioGroup>
      {kind === "pharmacy" && (
        <div className="space-y-1.5">
          <p className="text-xs text-slate-500">Rôles visés dans la pharmacie (aucun coché = tous).</p>
          <div className="grid grid-cols-2 gap-1.5">
            {roles.map((r) => (
              <label key={r.code} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid={`${idPrefix}-role-${r.code}`}>
                <Checkbox checked={audience.includes(r.code)} onCheckedChange={() => toggle(r.code)} />{r.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ItemMeta({ item }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
      <span className={`rounded-full px-2 py-0.5 font-medium ${item.required ? "bg-jamora/10 text-jamora" : "bg-slate-100 text-slate-600"}`}>
        {item.required ? "Obligatoire" : "Facultatif"}
      </span>
      {item.duration_minutes ? <span className="inline-flex items-center gap-1"><Clock size={12} />{formatMinutes(item.duration_minutes)}</span> : null}
      {item.pass_threshold ? <span>Réussite à {item.pass_threshold} %</span> : null}
    </div>
  );
}

// What a version teaches, module by module: what a trainer reads before a session.
export function ProgramPreview({ items, testid = "program-preview" }) {
  const totals = programTotals(items);
  return (
    <Card className="p-4 space-y-4" data-testid={testid}>
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Aperçu du programme</h3>
        <p className="text-xs text-slate-500" data-testid={`${testid}-summary`}>{programSummary(totals)}</p>
      </div>
      {items.length === 0 && <p className="text-sm text-slate-500">Ajoutez des modules pour voir le déroulé.</p>}
      <ol className="space-y-3">
        {items.map((item, index) => (
          <li key={item.module_id} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`${testid}-item-${index}`}>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-2xs font-semibold text-slate-600">{index + 1}</span>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm font-medium text-slate-900 break-words">{item.title}</p>
                <ItemMeta item={item} />
              </div>
            </div>
            {item.missing && <p className="text-xs text-amber-800">Ce module n'existe plus : retirez-le du programme.</p>}
            {item.module_active === false && <p className="text-xs text-amber-800">Module désactivé : il ne pourra pas être publié tel quel.</p>}
            {(item.objectives || []).length > 0 ? (
              <ul className="space-y-1 pl-7">
                {item.objectives.map((o) => (
                  <li key={o.id} className="flex items-start gap-1.5 text-sm text-slate-700"><Target size={13} className="mt-0.5 shrink-0 text-slate-400" />{o.text}</li>
                ))}
              </ul>
            ) : !item.missing && <p className="pl-7 text-xs text-amber-800">Aucun objectif : à compléter dans le module.</p>}
            {(item.features || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 pl-7">
                {item.features.map((f) => <span key={f.code} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{f.label}</span>)}
              </div>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}

// Pick modules to add to a draft: the active ones not already in it.
export function AddModuleDialog({ open, onClose, modules, excluded, onAdd }) {
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState([]);
  const available = useMemo(() => filterByWords(modules.filter((m) => m.active && !excluded.includes(m.id)), search,
    (m) => `${m.title} ${(m.objectives || []).map((o) => o.text).join(" ")} ${(m.features || []).map((f) => f.label).join(" ")}`), [modules, excluded, search]);
  const close = () => { setSearch(""); setChosen([]); onClose(); };
  const toggle = (id) => setChosen((all) => (all.includes(id) ? all.filter((x) => x !== id) : [...all, id]));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="program-add-module-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter des modules</DialogTitle>
          <DialogDescription>Ils s'ajoutent à la fin du programme, obligatoires ; vous pourrez les réordonner.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un module" className="pl-8 text-base md:text-sm"
            aria-label="Rechercher un module" data-testid="program-add-module-search" />
        </div>
        <div className="max-h-80 overflow-y-auto scrollbar-thin -mx-1 px-1 space-y-1">
          {available.length === 0 && (
            <p className="text-sm text-slate-500 py-4 text-center">
              {modules.some((m) => m.active && !excluded.includes(m.id)) ? "Aucun module ne correspond." : (
                <>Aucun autre module actif. <Link to="/formations/modules/nouveau" className="text-jamora underline">Créer un module</Link></>
              )}
            </p>
          )}
          {available.map((m) => (
            <label key={m.id} className="flex items-start gap-2.5 rounded-md px-2 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`program-add-module-option-${m.id}`}>
              <Checkbox checked={chosen.includes(m.id)} onCheckedChange={() => toggle(m.id)} className="mt-0.5" />
              <span className="min-w-0">
                <span className="block text-sm text-slate-800 break-words">{m.title}</span>
                <span className="block text-xs text-slate-500">
                  {[`${(m.objectives || []).length} objectif${(m.objectives || []).length > 1 ? "s" : ""}`, formatMinutes(m.duration_minutes)].filter(Boolean).join(" · ")}
                </span>
              </span>
            </label>
          ))}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={close}>Annuler</Button>
          <Button onClick={() => { onAdd(chosen); close(); }} disabled={!chosen.length} data-testid="program-add-module-confirm">
            Ajouter{chosen.length ? ` (${chosen.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FieldLabel({ htmlFor, children, hint }) {
  return (
    <div className="space-y-0.5">
      <Label htmlFor={htmlFor}>{children}</Label>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}
